<script setup lang="ts">
import { pwaConfig } from '@/utils/pwaTools'
import { computed, ref } from 'vue'

const enabled = computed(() => {
  return pwaConfig.runningInStandaloneMode
})

const containerRef = ref<HTMLElement | null>(null)
const pullDistance = ref(0)
const isRefreshing = ref(false)
const startY = ref(0)

// Configuration
const THRESHOLD = 90
const RESISTANCE = 2.5 // Higher = harder to pull

const handleTouchStart = (e: TouchEvent) => {
  const target = e.target as HTMLElement

  // 1. Check if the user is touching something that is already scrolled down
  // If the element (or its parents) is scrolled, we should NOT pull to refresh
  const isNestedScrolling = (el: HTMLElement | null): boolean => {
    if (!el || el === containerRef.value) return false
    if (el.scrollTop > 0) return true
    return isNestedScrolling(el.parentElement)
  }

  if (isNestedScrolling(target)) {
    startY.value = 0 // Disable PTR for this gesture
    return
  }

  // 2. Only track if our main container is at the very top
  if (containerRef.value && containerRef.value.scrollTop <= 0) {
    startY.value = e.touches[0].pageY
  }
}

const handleTouchMove = (e: TouchEvent) => {
  if (isRefreshing.value || startY.value === 0) return

  const currentY = e.touches[0].pageY
  const diff = currentY - startY.value

  // 3. Only trigger pull-down if the movement is downward
  // and we aren't scrolling inside a nested element
  if (diff > 0 && containerRef.value && containerRef.value.scrollTop <= 0) {
    pullDistance.value = diff / RESISTANCE

    // This stops the browser from scrolling the nested content
    // while the pull-to-refresh indicator is growing
    if (e.cancelable) e.preventDefault()
  }
}

const handleTouchEnd = () => {
  if (pullDistance.value >= THRESHOLD) {
    triggerRefresh()
  } else {
    // Snap back if threshold wasn't met
    pullDistance.value = 0
  }
  startY.value = 0
}

const triggerRefresh = async () => {
  isRefreshing.value = true
  pullDistance.value = THRESHOLD // Keep indicator visible

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    for (const reg of registrations) {
      if (reg.waiting) {
        // Tell the new service worker to take over immediately
        reg.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
      // Force an update check
      await reg.update()
    }
  }

  // Trigger reload
  setTimeout(() => {
    window.location.reload()
  }, 500)
}
</script>

<template>
  <div
    v-if="enabled"
    ref="containerRef"
    class="ptr-viewport"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <div
      class="ptr-header"
      :style="{
        height: pullDistance + 'px',
        opacity: pullDistance > 10 ? 1 : 0,
      }"
    >
      <div class="ptr-content">
        <div v-if="!isRefreshing" class="arrow" :class="{ rotated: pullDistance > THRESHOLD }">
          ↓
        </div>
        <div v-else class="spinner"></div>
        <span>{{ pullDistance > THRESHOLD ? 'Release to update' : 'Pull to update' }}</span>
      </div>
    </div>

    <div class="app-content">
      <slot />
    </div>
  </div>
  <slot v-else />
</template>

<style scoped>
.ptr-viewport {
  height: 100svh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain; /* The iPad Landscape 'Fix' */
}

.ptr-header {
  width: 100%;
  display: flex;
  align-items: flex-end; /* Keep content at the bottom of the pulling area */
  justify-content: center;
  overflow: hidden;
  background-color: #f0f0f0;
  transition: height 0.2s cubic-bezier(0, 0, 0.2, 1);
}

.ptr-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.arrow {
  font-size: 24px;
  transition: transform 0.2s ease;
}

.arrow.rotated {
  transform: rotate(180deg);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #42b883;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
